import { useEffect, useMemo, useState } from 'react';
import { ChannelType } from '@trii/types/dist/Common/Channels';
import { IContact, IContactField, Property } from '@trii/types/dist/Contacts';
import UseEditContact from './types/UseEditContact';
import { ItemWithId, useMultipleSelect } from '../useMultipleSelect';
// Hooks
import useField from '../useField';
import useImage from '../useImage';
import useSelect from '../useSelect';
import useEditContactMethods from '../useEditContactMethods';
// Utils
import editContactHelper from '../utils/editContactHelper';
import dayjs, { Dayjs } from 'dayjs';
import { deleteAddress } from '../../services/api';

type UseEditContactArgs = {
  contact: IContact | null;
  customContactFields: IContactField[];
  baseUrl?: string;
  spaceId?: string;
  onEntityUpdated?: (entity: unknown) => void;
};

const useEditContact = ({
  contact,
  customContactFields,
  baseUrl,
  spaceId,
  onEntityUpdated,
}: UseEditContactArgs): UseEditContact => {
  const apiDeleteAddress = useMemo(
    () => async (payload: { contactId: string; contactAddressId: string; type: ChannelType }) => {
      if (!baseUrl || !spaceId) return;
      await deleteAddress({ baseUrl, spaceId, data: payload });
    },
    [baseUrl, spaceId],
  );

  const apiDispatch = async <T>(maybePromise: Promise<T> | T) => maybePromise;

  const { getEditedField } = editContactHelper;

  const [customProperties, setCustomProperties] = useState<Property[]>([]);
  // const [selectedBusiness, setSelectedBusiness] = useState<SimplifiedBusiness[]>([]);
  const contactFirstname = useField('text', contact?.firstName || '');
  const contactLastname = useField('text', contact?.lastName || '');
  const contactImage = useImage({
    baseUrl,
    spaceId,
    entityType: 'contact',
    entityId: contact?.id ? String(contact.id) : undefined,
    initialImageUrl: contact?.imageUrl,
  });
  const [birthDate, setBirthDate] = useState<Dayjs | null>(
    contact?.birthDate ? dayjs(contact.birthDate) : null,
  );
  const contactMethods = useEditContactMethods({
    entityId: contact?.id,
    initial: contact,
    dispatch: apiDispatch,
    deleteAddress: apiDeleteAddress,
    onEntityUpdated,
  });

  const {
    field: {
      imsFacebooks,
      imsMercadolibre,
      imsWebchats,
      imsInstagrams,
      imsWhatsapp,
      emails,
      phoneNumbers,
      imsRCS,
    },
    action: {
      addPhone,
      addEmail,
      addWhatsapp,
      addRCS,
      addWhatsappVerified,
      replaceAddress,
      handleAddressFieldChange,
      handleNoteFieldChange,
      handleDeleteAddress,
      resetToInitialContactInformation,
    },
    state: { isDeletingAddress },
  } = contactMethods;
  //
  const labelMultipleSelect = useMultipleSelect({
    initialState: contact?.tags as ItemWithId[],
  });
  const contactPropietary = useSelect(contact?.owner);
  // const contactListSelectState = useSelect(contact?.listId);
  const homeAddress = useField('text', contact?.address1?.street);
  const secondaryHomeAddress = useField('text', contact?.address2?.street);
  const city = useField('text', contact?.address1?.city);
  const secondaryCity = useField('text', contact?.address2?.city);
  const state = useField('text', contact?.address1?.state);
  const secondaryState = useField('text', contact?.address2?.state);
  const postalCode = useField('text', contact?.address1?.zipcode);
  const secondaryPostalCode = useField('text', contact?.address2?.zipcode);
  const country = useField('text', contact?.address1?.country);
  const secondaryCountry = useField('text', contact?.address2?.country);

  // Actions
  const getEditedContact = () => {
    if (!contact) return null;
    const currentContact = contact;

    const modifiedTags = labelMultipleSelect.attributes.value.map((tag) => ({
      id: tag.id,
      name: tag.name,
    }));
    const modifiedPhones = phoneNumbers.map((phoneNumber) => {
      const { id, ...rest } = phoneNumber;
      return rest;
    });
    const modifiedEmails = emails.map((email) => {
      const { id, ...rest } = email;
      return rest;
    });

    const editedContact = {
      id: currentContact.id,
      owner: contactPropietary.attributes.value,
      firstName: contactFirstname.attributes.value,
      lastName: contactLastname.attributes.value,
      // listId: contactListSelectState.attributes.value,
      address1: {
        ...getEditedField('street', currentContact.address1?.street, homeAddress.attributes.value),
        ...getEditedField('city', currentContact.address1?.street, city.attributes.value),
        ...getEditedField('state', currentContact.address1?.street, state.attributes.value),
        ...getEditedField('zipcode', currentContact.address1?.street, postalCode.attributes.value),
        ...getEditedField('country', currentContact.address1?.street, country.attributes.value),
      },
      address2: {
        ...getEditedField(
          'street',
          currentContact.address2?.street,
          secondaryHomeAddress.attributes.value,
        ),
        ...getEditedField('city', currentContact.address2?.street, secondaryCity.attributes.value),
        ...getEditedField(
          'state',
          currentContact.address2?.street,
          secondaryState.attributes.value,
        ),
        ...getEditedField(
          'zipcode',
          currentContact.address2?.street,
          secondaryPostalCode.attributes.value,
        ),
        ...getEditedField(
          'country',
          currentContact.address2?.street,
          secondaryCountry.attributes.value,
        ),
      },
      emails: modifiedEmails,
      phones: modifiedPhones,
      // ims_whatsapp: modifiedImsWhatsapp,
      tags: modifiedTags,
      imageUrl: contactImage.imageUrl ? contactImage.removeParams(contactImage.imageUrl) : null,
      properties: customProperties,
      // businessId: selectedBusiness[0]?.businessId || '',
      // businessName: selectedBusiness[0]?.businessName || '',
      birthDate: birthDate?.toDate(),
    };

    return editedContact;
  };
  // contact methods/actions are handled by useEditContactMethods
  function resetSelectedBusiness() {
    if (!contact) return;
    if (!contact.businessId || !contact.businessName) {
      // setSelectedBusiness([]);
      return;
    }
    // setSelectedBusiness([
    //   { businessId: contact.businessId, businessName: contact.businessName },
    // ]);
  }

  function resetBirthDate() {
    setBirthDate(contact?.birthDate ? dayjs(contact.birthDate) : null);
  }
  function resetCustomProperty(nameKey: string) {
    if (!contact) return;

    const normalizeNameKey = (input: string) =>
      input.trim().toLowerCase().replace(/\s+/g, '-').replace(/-+/g, '-');

    const defaultProperty =
      contact.properties.find((property) => property.nameKey === nameKey) ??
      contact.properties.find(
        (property) => normalizeNameKey(property.nameKey) === normalizeNameKey(nameKey),
      );

    if (!defaultProperty) {
      console.error(`No se encontró una propiedad con nameKey: ${nameKey}`);
      return; // Salir de la función si no se encuentra la propiedad
    }

    const updatedProperties = customProperties.map((property) =>
      property.nameKey === nameKey ? { ...property, value: defaultProperty.value } : property,
    );
    setCustomProperties(updatedProperties);
  }

  const getFilteredCustomProperties = (): Property[] => {
    if (!contact) return [];

    return customContactFields.map((customField) => {
      const normalizeNameKey = (input: string) =>
        input.trim().toLowerCase().replace(/\s+/g, '-').replace(/-+/g, '-');

      const existingProperty =
        contact.properties.find((property) => property.nameKey === customField.nameKey) ??
        contact.properties.find(
          (property) =>
            normalizeNameKey(property.nameKey) === normalizeNameKey(customField.nameKey),
        );

      if (existingProperty) {
        return {
          ...existingProperty,
          nameKey: customField.nameKey,
          type: (existingProperty.type ?? customField.type)!,
          value: existingProperty.value,
        };
      }

      const existingLocalProperty = customProperties.find(
        (property) => property.nameKey === customField.nameKey,
      );

      return {
        nameKey: customField.nameKey,
        type: customField.type!,
        value: existingLocalProperty ? existingLocalProperty.value : '',
      };
    });
  };

  useEffect(() => {
    if (contact && customContactFields) {
      setCustomProperties(getFilteredCustomProperties());
    }
  }, [contact, customContactFields]);

  useEffect(() => {
    if (contact?.tags) {
      labelMultipleSelect.actions.setValue(contact.tags);
    }
    if (contact?.businessId && contact.businessName) {
      // setSelectedBusiness([
      //   { businessId: contact.businessId, businessName: contact.businessName },
      // ]);
    } else {
      // setSelectedBusiness([]);
    }
  }, [contact]);

  return {
    field: {
      birthDate,
      imsWhatsapp,
      imsRCS,
      contactFirstname,
      contactLastname,
      contactImage,
      contactPropietary,
      imsMercadolibre,
      imsFacebooks,
      imsWebchats,
      imsInstagrams,
      emails,
      phoneNumbers,
      homeAddress,
      secondaryHomeAddress,
      city,
      secondaryCity,
      state,
      secondaryState,
      postalCode,
      secondaryPostalCode,
      country,
      secondaryCountry,
      customProperties,
      labelMultipleSelect,
      // selectedBusiness,
    },
    action: {
      setBirthDate,
      setCustomProperties,
      getEditedContact,
      handleAddressFieldChange,
      handleNoteFieldChange,
      handleDeleteAddress,
      addPhone,
      addEmail,
      addWhatsapp,
      addRCS,
      replaceAddress,
      // setSelectedBusiness,
      addWhatsappVerified,
      resetToInitialContactInformation,
      resetSelectedBusiness,
      resetBirthDate,
      resetCustomProperty,
    },
    state: {
      isDeletingAddress,
    },
  };
};

export default useEditContact;
